import { useEffect, useState, useCallback, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useScreenTour } from "@/hooks/useScreenTour";
import { PageHeader } from "@/components/PageHeader";
import { EntityAutocomplete } from "@/components/EntityAutocomplete";
import { EmptyFiltered } from "@/components/EmptyFiltered";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Pencil, Download, FileSpreadsheet, ChevronsUpDown } from "lucide-react";

const EMPTY = {
  fecha: new Date().toISOString().slice(0, 10), sentido: "impo", unidad: "", placas: "",
  operador: "", cliente_nombre: "", client_id: null, client_label: "",
  bodega: "", referencia: "", carta_porte_nac: "", carta_porte_cruce: "", factura: "",
  status: "", notas: "", load_id: null, load_label: "",
};

const EMPTY_FILTERS = {
  sentido: "all", fecha_desde: "", fecha_hasta: "", cliente_nombre: "", unidad: "", referencia: "", status: "",
};

function SentidoBadge({ sentido }) {
  return sentido === "impo" ? (
    <Badge className="border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-50">Impo</Badge>
  ) : (
    <Badge className="border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-50">Expo</Badge>
  );
}

function LoadLinkPicker({ label, onSelect, onClear, testId }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const boxRef = useRef(null);

  useEffect(() => {
    const onClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/loads", { params: { search: query, limit: 8, estado_registro: "all" } });
        setResults(data.items);
      } catch {
        /* silent */
      }
    }, 250);
    return () => clearTimeout(t);
  }, [query, open]);

  return (
    <div className="relative" ref={boxRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid={testId}
        className="flex w-full items-center justify-between rounded-md border border-input bg-white px-3 py-2 text-sm"
      >
        <span className={`truncate ${label ? "text-foreground" : "text-muted-foreground"}`}>
          {label || "Buscar número de load..."}
        </span>
        <ChevronsUpDown className="h-4 w-4 shrink-0 text-muted-foreground" />
      </button>
      {open && (
        <div className="absolute z-50 mt-1 w-full rounded-md border border-border bg-white shadow-lg">
          <div className="p-2">
            <Input autoFocus value={query} onChange={(e) => setQuery(e.target.value)}
              placeholder="Número o referencia..." data-testid={`${testId}-search`} />
          </div>
          <div className="max-h-56 overflow-y-auto">
            {results.length === 0 && <div className="px-3 py-2 text-xs text-muted-foreground">Sin resultados.</div>}
            {results.map((r) => (
              <button
                key={r.id}
                type="button"
                onClick={() => { onSelect(r); setOpen(false); setQuery(""); }}
                data-testid={`${testId}-option-${r.numero_load}`}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-slate-50"
              >
                <span className="font-mono text-xs text-muted-foreground">{r.numero_load}</span>
                <span className="truncate text-xs">{r.client_nombre} — {r.referencia_cliente}</span>
              </button>
            ))}
          </div>
          {label && (
            <div className="border-t border-border p-2">
              <Button type="button" size="sm" variant="ghost" className="w-full"
                onClick={() => { onClear(); setOpen(false); }} data-testid={`${testId}-clear`}>
                Quitar vínculo
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function Cruces() {
  const { can } = useAuth();
  useScreenTour("cruces");
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState({ items: [], total: 0, counts: { todos: 0, impo: 0, expo: 0 } });
  const [filters, setFilters] = useState(() => {
    const sentidoParam = searchParams.get("sentido");
    const sentido = ["impo", "expo"].includes(sentidoParam) ? sentidoParam : sessionStorage.getItem("cruces_sentido") || "all";
    return { ...EMPTY_FILTERS, sentido, referencia: searchParams.get("referencia") || "" };
  });
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const limit = 50;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { skip: page * limit, limit };
      Object.entries(filters).forEach(([k, v]) => { if (v && v !== "all") params[k] = v; });
      const { data } = await api.get("/cruces", { params });
      setData(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [filters, page]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const setFilter = (k, v) => { setPage(0); setFilters((f) => ({ ...f, [k]: v })); };
  const setSentido = (v) => {
    setFilter("sentido", v);
    sessionStorage.setItem("cruces_sentido", v);
    setSearchParams((prev) => { const p = new URLSearchParams(prev); if (v === "all") p.delete("sentido"); else p.set("sentido", v); return p; });
  };
  const clearFilters = () => { setFilters(EMPTY_FILTERS); sessionStorage.removeItem("cruces_sentido"); setSearchParams({}); };

  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (c) => {
    setEditing(c);
    setForm({
      fecha: c.fecha, sentido: c.sentido, unidad: c.unidad || "", placas: c.placas || "",
      operador: c.operador || "", cliente_nombre: c.cliente_nombre || "",
      client_id: c.client_id || null, client_label: c.client_id ? c.cliente_nombre : "",
      bodega: c.bodega || "", referencia: c.referencia || "", carta_porte_nac: c.carta_porte_nac || "",
      carta_porte_cruce: c.carta_porte_cruce || "", factura: c.factura || "", status: c.status || "",
      notas: c.notas || "", load_id: c.load_id || null, load_label: c.load_numero || "",
    });
    setOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        fecha: form.fecha, sentido: form.sentido, unidad: form.unidad, placas: form.placas,
        operador: form.operador, cliente_nombre: form.cliente_nombre, client_id: form.client_id || null,
        bodega: form.bodega, referencia: form.referencia, carta_porte_nac: form.carta_porte_nac,
        carta_porte_cruce: form.carta_porte_cruce, factura: form.factura, status: form.status,
        notas: form.notas, load_id: form.load_id || null,
      };
      let res;
      if (editing) res = await api.put(`/cruces/${editing.id}`, payload);
      else res = await api.post("/cruces", payload);
      toast.success(editing ? "Cruce actualizado" : "Cruce creado");
      if (res.data.aviso_duplicado) {
        toast.warning("Ya existía un cruce con la misma fecha, referencia y sentido — se guardó igual");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const exportFile = async (fmt) => {
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => { if (v && v !== "all") params[k] = v; });
      const res = await api.get(`/cruces/export/${fmt}`, { params, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `cruces.${fmt}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch {
      toast.error("No se pudo exportar");
    }
  };

  const totalPages = Math.max(1, Math.ceil(data.total / limit));

  return (
    <div>
      <PageHeader
        title="Cruces"
        subtitle="Los cruces de frontera de tu flota, separados en importaciones y exportaciones."
        actions={
          <div className="flex gap-2" data-tour="cruces-export">
            <Button variant="outline" size="sm" onClick={() => exportFile("csv")} className="gap-1" data-testid="cruces-export-csv">
              <Download className="h-4 w-4" /> CSV
            </Button>
            <Button variant="outline" size="sm" onClick={() => exportFile("xlsx")} className="gap-1" data-testid="cruces-export-xlsx">
              <FileSpreadsheet className="h-4 w-4" /> Excel
            </Button>
            {can("cruces", "crear") && (
              <Button onClick={openCreate} className="gap-2" data-testid="new-cruce-btn">
                <Plus className="h-4 w-4" /> Nuevo cruce
              </Button>
            )}
          </div>
        }
      />

      <div className="mb-4" data-tour="cruces-tabs">
        <Tabs value={filters.sentido} onValueChange={setSentido}>
          <TabsList>
            <TabsTrigger value="all" data-testid="cruces-tab-todos">Todos ({data.counts?.todos ?? 0})</TabsTrigger>
            <TabsTrigger value="impo" data-testid="cruces-tab-impo">Importaciones ({data.counts?.impo ?? 0})</TabsTrigger>
            <TabsTrigger value="expo" data-testid="cruces-tab-expo">Exportaciones ({data.counts?.expo ?? 0})</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      <div className="mb-4 grid grid-cols-1 gap-2 sm:grid-cols-3 lg:grid-cols-6" data-tour="cruces-filtros">
        <Input type="date" value={filters.fecha_desde} onChange={(e) => setFilter("fecha_desde", e.target.value)} data-testid="cruce-filter-fecha-desde" />
        <Input type="date" value={filters.fecha_hasta} onChange={(e) => setFilter("fecha_hasta", e.target.value)} data-testid="cruce-filter-fecha-hasta" />
        <Input placeholder="Cliente" value={filters.cliente_nombre} onChange={(e) => setFilter("cliente_nombre", e.target.value)} data-testid="cruce-filter-cliente" />
        <Input placeholder="Unidad" value={filters.unidad} onChange={(e) => setFilter("unidad", e.target.value)} data-testid="cruce-filter-unidad" />
        <Input placeholder="Referencia" value={filters.referencia} onChange={(e) => setFilter("referencia", e.target.value)} data-testid="cruce-filter-referencia" />
        <Input placeholder="Status" value={filters.status} onChange={(e) => setFilter("status", e.target.value)} data-testid="cruce-filter-status" />
      </div>

      <div className="overflow-x-auto rounded-md border border-border bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Fecha</TableHead>
              <TableHead>Sentido</TableHead>
              <TableHead>Cliente</TableHead>
              <TableHead>Referencia</TableHead>
              <TableHead>Unidad</TableHead>
              <TableHead>Operador</TableHead>
              <TableHead data-tour="cruces-carta-porte-column">Carta porte</TableHead>
              <TableHead>Factura</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Load</TableHead>
              <TableHead className="text-right">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.items.map((c) => (
              <TableRow key={c.id} data-testid={`cruce-row-${c.id}`}>
                <TableCell className="font-mono text-xs">{c.fecha}</TableCell>
                <TableCell><SentidoBadge sentido={c.sentido} /></TableCell>
                <TableCell className="max-w-[140px] truncate text-sm">{c.cliente_nombre || "—"}</TableCell>
                <TableCell className="text-xs">{c.referencia || "—"}</TableCell>
                <TableCell className="text-xs">{c.unidad || "—"}</TableCell>
                <TableCell className="text-xs">{c.operador || "—"}</TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  {c.carta_porte_nac && <div>Nac: {c.carta_porte_nac}</div>}
                  {c.carta_porte_cruce && <div>Cruce: {c.carta_porte_cruce}</div>}
                  {!c.carta_porte_nac && !c.carta_porte_cruce && "—"}
                </TableCell>
                <TableCell className="text-xs">{c.factura || "—"}</TableCell>
                <TableCell className="text-xs">{c.status || "—"}</TableCell>
                <TableCell className="text-xs">
                  {c.load_numero ? (
                    <Link to={`/loads/${c.load_id}`} className="font-mono text-primary underline" data-testid={`cruce-row-${c.id}-load-link`}>
                      {c.load_numero}
                    </Link>
                  ) : "—"}
                </TableCell>
                <TableCell className="text-right">
                  {can("cruces", "editar") && (
                    <Button variant="ghost" size="icon" onClick={() => openEdit(c)} data-testid={`edit-cruce-${c.id}`}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {!loading && data.items.length === 0 && (
              <EmptyFiltered colSpan={11} onClear={clearFilters} testId="cruces-empty" mensaje="No hay cruces con estos filtros." />
            )}
          </TableBody>
        </Table>
      </div>

      <div className="mt-4 flex items-center justify-between text-sm text-muted-foreground">
        <span data-testid="cruces-total-count">{data.total} cruce(s)</span>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>Anterior</Button>
          <span>Pág. {page + 1} de {totalPages}</span>
          <Button variant="outline" size="sm" disabled={page + 1 >= totalPages} onClick={() => setPage((p) => p + 1)}>Siguiente</Button>
        </div>
      </div>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-lg">
          <SheetHeader><SheetTitle className="font-heading">{editing ? "Editar cruce" : "Nuevo cruce"}</SheetTitle></SheetHeader>

          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Fecha *">
              <Input type="date" value={form.fecha} onChange={(e) => setForm({ ...form, fecha: e.target.value })} data-testid="cruce-form-fecha" />
            </Field>
            <Field label="Sentido *">
              <Select value={form.sentido} onValueChange={(v) => setForm({ ...form, sentido: v })}>
                <SelectTrigger data-testid="cruce-form-sentido"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="impo">Importación</SelectItem>
                  <SelectItem value="expo">Exportación</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Cliente" className="sm:col-span-2">
              <Input value={form.cliente_nombre} onChange={(e) => setForm({ ...form, cliente_nombre: e.target.value })} data-testid="cruce-form-cliente" />
            </Field>
            <Field label="Vincular con cliente del catálogo (opcional)" className="sm:col-span-2">
              <EntityAutocomplete type="clients" value={form.client_id} label={form.client_label}
                onSelect={(c) => setForm({ ...form, client_id: c.id, client_label: `${c.codigo} — ${c.razon_social}`, cliente_nombre: form.cliente_nombre || c.razon_social })}
                testId="cruce-form-client-link" />
              {form.client_id && (
                <button type="button" className="mt-1 text-xs text-muted-foreground underline" onClick={() => setForm({ ...form, client_id: null, client_label: "" })} data-testid="cruce-form-client-link-remove">
                  Quitar vínculo
                </button>
              )}
            </Field>
            <Field label="Unidad"><Input value={form.unidad} onChange={(e) => setForm({ ...form, unidad: e.target.value })} data-testid="cruce-form-unidad" /></Field>
            <Field label="Placas"><Input value={form.placas} onChange={(e) => setForm({ ...form, placas: e.target.value })} data-testid="cruce-form-placas" /></Field>
            <Field label="Operador"><Input value={form.operador} onChange={(e) => setForm({ ...form, operador: e.target.value })} data-testid="cruce-form-operador" /></Field>
            <Field label="Bodega"><Input value={form.bodega} onChange={(e) => setForm({ ...form, bodega: e.target.value })} data-testid="cruce-form-bodega" /></Field>
            <Field label="Referencia" className="sm:col-span-2"><Input value={form.referencia} onChange={(e) => setForm({ ...form, referencia: e.target.value })} data-testid="cruce-form-referencia" /></Field>
            <Field label="Carta porte nacional"><Input value={form.carta_porte_nac} onChange={(e) => setForm({ ...form, carta_porte_nac: e.target.value })} data-testid="cruce-form-carta-porte-nac" /></Field>
            <Field label="Carta porte cruce"><Input value={form.carta_porte_cruce} onChange={(e) => setForm({ ...form, carta_porte_cruce: e.target.value })} data-testid="cruce-form-carta-porte-cruce" /></Field>
            <Field label="Factura" className="sm:col-span-2"><Input value={form.factura} onChange={(e) => setForm({ ...form, factura: e.target.value })} data-testid="cruce-form-factura" /></Field>
            <Field label="Status" className="sm:col-span-2">
              <Input value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} placeholder="ENTREGADO, EN CRUCE, ..." data-testid="cruce-form-status" />
            </Field>
            <Field label="Load vinculado (opcional)" className="sm:col-span-2">
              <LoadLinkPicker label={form.load_label}
                onSelect={(l) => setForm({ ...form, load_id: l.id, load_label: l.numero_load })}
                onClear={() => setForm({ ...form, load_id: null, load_label: "" })}
                testId="cruce-form-load" />
            </Field>
            <Field label="Notas" className="sm:col-span-2">
              <Textarea value={form.notas} onChange={(e) => setForm({ ...form, notas: e.target.value })} data-testid="cruce-form-notas" />
            </Field>
          </div>

          <SheetFooter className="mt-6">
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={save} disabled={saving || !form.fecha || !form.sentido} data-testid="cruce-save-btn">
              {saving ? "Guardando..." : "Guardar"}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </div>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
